import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAppSelector } from "../State/Hooks/reduxHooks";
import { selectClientValue } from "../State/Slices/SelectedClientSlice";
import { ISavedSearch, ISaveSearchFilter, ISaveSearchFilterCommand } from "../Typings/SearchFilters";
import { apiPathSearchFilter, fetcher, secureFetcher } from "./queryHelpers";
import { useCurrentUser } from "./UserQueries";
import { useAuthToken } from "../State/Hooks/authHooks";

// See React Query v4 docs for more info on useQuery
// https://tanstack.com/query/v4/docs/quick-start

export function useSearchTagsQuery(clientId?: string) {
  const token = useAuthToken();
  const { clientId: selectedClientId } = useAppSelector(selectClientValue);
  const queryClientId = clientId ? clientId : selectedClientId;
  return useQuery<string[]>(
    ["searchTags", queryClientId],
    async () => {
      const response: string[] = await secureFetcher(apiPathSearchFilter + "tags/" + queryClientId, token);
      return response.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
    },
    {
      enabled: !!queryClientId && !!token,
      staleTime: 1000 * 60 * 10, // 10 minutes
    },
  );
}
export function useSearchTitlesQuery() {
  const token = useAuthToken();
  const selectedClient = useAppSelector(selectClientValue);
  return useQuery<string[]>(
    ["searchTitles", selectedClient.clientId],
    async () => {
      const response: string[] = await secureFetcher(
        apiPathSearchFilter + "titles/" + selectedClient.clientId,
        token,
      );
      return response.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
    },
    {
      enabled: !!selectedClient.clientId && !!token,
      staleTime: 1000 * 60 * 10, // 10 minutes
    },
  );
}

/**
 * @description Retrieves a list of saved searches for the currently selected client
 * and user
 *
 * @returns UseQueryResult<ISavedSearch[]>
 */
export function useSavedSearchesQuery(clientId?: string) {
  const token = useAuthToken();
  const { id: userId } = useCurrentUser();
  const { clientId: selectedClientId } = useAppSelector(selectClientValue);
  const queryClientId = clientId ? clientId : selectedClientId;
  return useQuery<ISavedSearch[]>(
    ["savedSearches", userId, queryClientId],
    () => secureFetcher(apiPathSearchFilter + queryClientId + "/" + userId, token),
    {
      enabled: !!queryClientId && !!userId && !!token,
    },
  );
}

/**
 * @description Retrieves a list of saved searches for the currently selected client
 * and user
 *
 * @returns UseQueryResult<ISavedSearch[]>
 */
export function useSavedCampaignsSearchesQuery(campaignId: string) {
  const token = useAuthToken();
  const { id: userId } = useCurrentUser();
  const { clientId } = useAppSelector(selectClientValue);
  let path = apiPathSearchFilter + clientId + "/" + userId;
  if (campaignId) {
    path += "?campaignId=" + campaignId;
  }
  return useQuery<ISavedSearch[]>(
    ["savedSearches", userId, clientId, campaignId],
    () => secureFetcher(path, token),
    {
      enabled: !!clientId && !!userId && !!token,
    },
  );
}

/**
 * Saves a new search with the given criteria
 *
 * Called using [variable].mutate(arg: ISearchBody)
 *
 * @returns UseMutationResult<boolean, unknown, ISearchBody, unknown>
 */
export const useSaveSearchFilterMutation = (clientId?: string) => {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  const { id: userId } = useCurrentUser();
  const { clientId: selectedClientId } = useAppSelector(selectClientValue);
  const queryClientId = clientId ? clientId : selectedClientId;
  return useMutation<boolean, unknown, ISaveSearchFilter>(
    (searchBody) => {
      const saveSearchFilterCommand: ISaveSearchFilterCommand = {
        ...searchBody,
        clientId: queryClientId,
        userId,
      };

      return fetcher(apiPathSearchFilter + "save", {
        method: "POST",
        body: JSON.stringify(saveSearchFilterCommand),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
    },
    {
      onSuccess: () => queryClient.invalidateQueries(["savedSearches"]),
    },
  );
};

/**
 * Deletes a given filter
 *
 * Called using [variable].mutate(arg: string)
 *
 * @returns UseMutationResult<boolean, unknown, ISearchBody, unknown>
 */
export const useDeleteSearchFilterMutation = (clientId?: string) => {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  const { clientId: selectedClientId } = useAppSelector(selectClientValue);
  const queryClientId = clientId ? clientId : selectedClientId;
  return useMutation<boolean, unknown, string>(
    (filterId) =>
      fetcher(apiPathSearchFilter + queryClientId + "/" + filterId, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
    {
      onSuccess: () => queryClient.invalidateQueries(["savedSearches"]),
    },
  );
};
